import { useEventLogging } from '@AppModels/Logging';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { Provider } from 'mobx-react';
import * as React from 'react';
import ReactModal from 'react-modal';
import { toast } from 'sonner';
import { EmailComposerContext } from '../../../../models/Email';
import { useEnvironment, useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { FileSendLinkPlaceholder, FirstNamePlaceholder, Token } from '../../../../models/Token';
import { isFilesFeatureEnabled } from '../../../../models/utils/accountUtils';
import { contentHasFileLinkToken } from '../../../../models/utils/richContentTokenUtils';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	defaultRichContentForEmailAutomationStep,
	getFileSizeStringValue,
} from '../../../../models/UiUtils';
import {
	useAutomationTemplateStepEmailAttachmentCreateMutation,
	useAutomationTemplateStepEmailAttachmentDeleteMutation,
	useAutomationTemplateStepEmailUpdateMutation,
	useAutomationTemplateStepHtmlNewsletterAttachmentCreateMutation,
	useAutomationTemplateStepHtmlNewsletterAttachmentDeleteMutation,
	useAutomationTemplateStepHtmlNewsletterUpdateMutation,
} from '../../../../queries';
import EmailAutomationGraphic from '../../../assets/email_automation_graphic.svg';
import { AIAssistantButton } from '../../../containers/AIAssistantButton';
import { UniversalAssistantChatWithinComposer } from '../../../containers/templates/AssistantChat';
import { baseStyleSheet } from '../../../styles/styles';
import { CloseButton } from '../../CloseButton';
import { EmailComposer } from '../../email/EmailComposer';
import { SendATestButton } from '../../email/SendATestButton';
import { ITestEmailResult, TestEmailModal } from '../../email/TestEmailModal';
import { LoadingSpinner } from '../../LoadingSpinner';
import { Modal } from '../../Modal';
import { SaveTemplate } from '../../SaveTemplate';
import {
	canSendAutomationStepTestEmail,
	getAutomationStepTestEmailNotice,
	useSendAutomationStepTestEmail,
} from '../automationStepTestEmail';
import { NotifyAttachmentsUnionModal } from './notifyAttachmentsUnion.modal';
import { styleSheet } from './styles';

interface Props {
	automationTemplateId: string;
	step: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep;
	className?: string;
	onRequestRemoveSavedEmailAttachment?(savedAttachment: Api.IFileAttachment): Promise<any>;
	readonly?: boolean;
	trigger?: Api.IResourceSelectorAutomationTrigger;
	styles?: StyleDeclarationValue[];
	onRequestClose?(result?: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep, canceled?: boolean): void;
	syncSubjectWithStep?: boolean;
	onAttachmentRemoved?: (data: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep) => void;
	impersonationContext?: Api.IImpersonationContext;
	defaultInitialContent?: Api.IRawRichTextContentState;
	onStepChanged?: () => void;
}

export function EditAutomationStepEmailComposerModal({
	automationTemplateId,
	step,
	trigger,
	readonly,
	onRequestClose,
	syncSubjectWithStep = false,
	onAttachmentRemoved,
	onStepChanged,
	impersonationContext,
	defaultInitialContent = defaultRichContentForEmailAutomationStep,
}: Props) {
	const isAdminView = useEnvironment()?.appType === 'admin';
	const userSession = useUserSession();
	const account = impersonationContext?.account ?? userSession.account.toJs();
	const canSendFileLinks =
		isFilesFeatureEnabled(account) &&
		!!account?.features?.files?.canSendFileLinks &&
		!!userSession.user?.userGroupPermissions?.featurePermissions?.files?.sendDownloadLink;
	const [sendAttachmentsAsLink, setSendAttachmentsAsLink] = React.useState(!!step.sendAttachmentsAsLink);
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging();
	const attachmentByteSizeLimit = Api.VmUtils.getMaxAttachmentByteCountForEmailConfigurations(
		impersonationContext
			? impersonationContext.account.emailProviderConfigurations
			: userSession.account.toJs().emailProviderConfigurations
	);
	const [attachments] = React.useState(
		new Api.AttachmentsToBeUploadedViewModel<File>([], Api.TextMessagingMaxLinkFileByteSize)
	);
	const [newFilesSize, setNewFilesSize] = React.useState(0);
	const [templateAttachments, setTemplateAttachments] = React.useState<Api.IFileAttachment[]>([]);
	const [flow] = React.useState(trigger?.resourceSelector);
	const [emailBodyEditorState, setEmailBodyEditorState] = React.useState(
		convertRawRichTextContentStateToRichContentEditorState(step.content ?? defaultInitialContent)
	);
	const [emailMessageTemplate, setEmailMessageTemplate] = React.useState<Api.ITemplate>(
		step.options?.templateReference?.templateId
			? {
					id: step.options.templateReference.templateId,
					name: step.options.templateReference.name,
				}
			: null
	);
	const [subject, setSubject] = React.useState(step.options.subject ?? step.subject);
	const [messageTemplateEdited, setMessageTemplateEdited] = React.useState(false);
	const [messageFieldError, setMessageFieldError] = React.useState<string>();
	const [subjectFieldError, setSubjectFieldError] = React.useState<string>();
	const canShowAiContentAssistant = (impersonationContext?.account ? impersonationContext.account : userSession.account)
		?.features?.contentGeneration?.enabled;
	const composerContext = { flow };
	const [showingAiContentGenWizard, setShowingAiContentGenWizard] = React.useState(false);
	const [showSaveTemplateModal, setShowSaveTemplateModal] = React.useState(false);
	const isNewsletterStep = step._type === 'HtmlNewsletterAutomationStep';
	const canSendTestEmail = canSendAutomationStepTestEmail(isAdminView, impersonationContext);
	const [isTestEmailModalOpen, setIsTestEmailModalOpen] = React.useState(false);
	const sendTestEmail = useSendAutomationStepTestEmail(impersonationContext);

	const onUseAIEmail = (emailSubject: string, emailContent: Api.IRawRichTextContentState) => {
		setEmailBodyEditorState(convertRawRichTextContentStateToRichContentEditorState(emailContent));
		if (emailSubject) {
			setSubject(emailSubject);
		}
	};
	const stopModalContextMenuPropagation = (event: React.MouseEvent) => {
		event.stopPropagation();
	};
	const [isNotifyAttachmentsUnionModalOpen, setIsNotifyAttachmentsUnionModalOpen] = React.useState(false);

	const clearErrorMessages = () => {
		if (!!messageFieldError || !!subjectFieldError) {
			setMessageFieldError('');
			setSubjectFieldError('');
		}
	};

	const onMessageEditorStateChanged = (editorState: Api.IRichContentEditorState) => {
		setEmailBodyEditorState(editorState);
		setEmailMessageTemplate(editorState.hasContent() ? emailMessageTemplate : null);
		setMessageFieldError('');
		setMessageTemplateEdited(editorState.hasContent() ? !!emailMessageTemplate : false);
		setSubjectFieldError('');
	};

	const onEmailMessageTemplateChanged = (template: Api.ITemplate) => {
		setEmailMessageTemplate(template);
		setMessageFieldError('');
		setSubjectFieldError('');
		if (template) {
			if (step.attachments?.length > 0) {
				setIsNotifyAttachmentsUnionModalOpen(true);
			}

			setEmailBodyEditorState(convertRawRichTextContentStateToRichContentEditorState(template.content));
			setSubject(template.subject || '');
			setTemplateAttachments(template.attachments ?? []);
		}
	};

	const onSubjectChanged = (_subject: string) => {
		setMessageFieldError('');
		setSubject(_subject);
		setSubjectFieldError('');
	};

	const onCancelButtonClicked = () => {
		onRequestClose(null, true);
	};

	const onSaveAsTemplateButtonClicked = () => {
		setShowSaveTemplateModal(true);
	};

	const handleTemplateSaveSuccess = (template: Api.ITemplate) => {
		setShowSaveTemplateModal(false);
		toast.success(`Template "${template.name}" has been saved successfully.`);
	};

	const handleTemplateSaveCancel = () => {
		setShowSaveTemplateModal(false);
	};
	const emailStepUpdateMutation = useAutomationTemplateStepEmailUpdateMutation({
		onError: error => {
			logApiError('SaveEamilAutomationStep-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: data => {
			onRequestClose(data, false);
		},
	});
	const newsletterStepUpdateMutation = useAutomationTemplateStepHtmlNewsletterUpdateMutation({
		onError: error => {
			logApiError('SaveNewsletterAutomationStep-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: data => {
			onRequestClose(data, false);
		},
	});
	const emailAttachmentCreateMutation = useAutomationTemplateStepEmailAttachmentCreateMutation();
	const newsletterAttachmentCreateMutation = useAutomationTemplateStepHtmlNewsletterAttachmentCreateMutation();
	const attachmentCreateMutation = isNewsletterStep
		? newsletterAttachmentCreateMutation
		: emailAttachmentCreateMutation;
	const stepUpdateMutation = isNewsletterStep ? newsletterStepUpdateMutation : emailStepUpdateMutation;
	const emailAttachmentDeleteMutation = useAutomationTemplateStepEmailAttachmentDeleteMutation();
	const newsletterAttachmentDeleteMutation = useAutomationTemplateStepHtmlNewsletterAttachmentDeleteMutation();
	const attachmentDeleteMutation = isNewsletterStep
		? newsletterAttachmentDeleteMutation
		: emailAttachmentDeleteMutation;
	const saving =
		attachmentCreateMutation.isPending || stepUpdateMutation.isPending || attachmentDeleteMutation.isPending;
	// Correctness relies on the modal being unmounted on close (not hidden via isOpen). Each open
	// starts fresh state, so cancelled removals are never persisted.
	const [pendingDeletions, setPendingDeletions] = React.useState<Api.IFileAttachment[]>([]);

	const templateAttachmentsSize = templateAttachments.reduce((sum, a) => sum + a.fileSize, 0);
	const remainingStepAttachments = (step.attachments ?? []).filter(a => !pendingDeletions.some(d => d.id === a.id));
	const remainingStepAttachmentsSize = remainingStepAttachments.reduce((sum, a) => sum + a.fileSize, 0);
	const totalAttachmentSize = newFilesSize + templateAttachmentsSize + remainingStepAttachmentsSize;
	const isTotalSizeLarge = totalAttachmentSize > attachmentByteSizeLimit;

	const handleRequestRemoveSavedEmailAttachment = async (attachment: Api.IFileAttachment) => {
		setPendingDeletions(prev => [...prev, attachment]);
	};

	const handleToggleSendAttachmentsAsLink = (value: boolean) => {
		setSendAttachmentsAsLink(value);
		setEmailBodyEditorState(prev => {
			const rawContent = prev.getRawRichTextContent();
			const currentHtml = Api.getContentHtmlStringValueFromRawRichTextContentState(rawContent) ?? '';
			const allSymbols = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];
			if (value) {
				if (!allSymbols.some(sym => currentHtml.includes(sym))) {
					return convertRawRichTextContentStateToRichContentEditorState(
						Api.createRawRichTextContentStateWithHtmlStringValue(
							`${currentHtml}<p>${FileSendLinkPlaceholder.symbol}</p>`
						)
					);
				}
				return prev;
			}
			let newHtml = Token.replaceToken(currentHtml, FileSendLinkPlaceholder, '');
			for (const sym of allSymbols) {
				newHtml = newHtml.split(`<p>${sym}</p>`).join('').split(sym).join('');
			}
			return convertRawRichTextContentStateToRichContentEditorState(
				Api.createRawRichTextContentStateWithHtmlStringValue(newHtml)
			);
		});
	};

	React.useEffect(() => {
		if (isTotalSizeLarge && !sendAttachmentsAsLink) {
			handleToggleSendAttachmentsAsLink(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTotalSizeLarge]);

	const onCustomFileChanged = () => {
		setNewFilesSize(attachments.byteCount);
	};

	const validateDraft = (purpose: 'saving' | 'sending a test'): Api.IRawRichTextContentState | null => {
		if (!subject) {
			setMessageFieldError('');
			setSubjectFieldError(`Please enter a subject before ${purpose}.`);
			return null;
		}

		if (!emailBodyEditorState || (!!emailBodyEditorState && !emailBodyEditorState.hasContent())) {
			setMessageFieldError(`Please enter a message before ${purpose}.`);
			setSubjectFieldError('');
			return null;
		}

		const newContent = emailBodyEditorState.getRawRichTextContent();
		if (!Token.isFirstNameTokenFormattedCorrectlyLoose(newContent)) {
			errorMessages.push({
				messages: [
					`The first name token does not appear to be formatted properly. It should be ${FirstNamePlaceholder.symbol}.`,
				],
			});
			return null;
		}

		if (canSendFileLinks) {
			const hasToken = contentHasFileLinkToken(newContent);
			if (sendAttachmentsAsLink && !hasToken) {
				setMessageFieldError('A file send merge field must be included.');
				setSubjectFieldError('');
				return null;
			}
			if (!sendAttachmentsAsLink && hasToken) {
				setMessageFieldError('No files were available to send securely.');
				setSubjectFieldError('');
				return null;
			}
		}

		const saveTimeTotal =
			attachments.byteCount +
			templateAttachments.reduce((count, x) => count + x.fileSize, 0) +
			remainingStepAttachments.reduce((count, x) => count + x.fileSize, 0);
		const saveTimeLimit = sendAttachmentsAsLink ? Api.TextMessagingMaxLinkFileByteSize : attachmentByteSizeLimit;
		const hasExceededMaxByteCount = saveTimeTotal > saveTimeLimit;

		if (hasExceededMaxByteCount) {
			setMessageFieldError(`You've exceeded the attachment size limit of ${getFileSizeStringValue(saveTimeLimit)}.`);
			setSubjectFieldError('');
			return null;
		}

		return newContent;
	};

	const createTemplateReference = (): Api.ITemplateReference | null =>
		emailMessageTemplate?.id
			? {
					isCustomized: !!messageTemplateEdited,
					isSystemTemplate:
						emailMessageTemplate?.scope === Api.TemplateScope.System ||
						emailMessageTemplate?.scope === Api.TemplateScope.Industry,
					name: emailMessageTemplate.name,
					templateId: emailMessageTemplate.id,
				}
			: null;

	const onSendTestButtonClicked = () => {
		if (validateDraft('sending a test')) {
			setIsTestEmailModalOpen(true);
		}
	};

	const onSendTestEmail = (contactId: string) =>
		sendTestEmail({
			contactId,
			email: {
				content: emailBodyEditorState.getRawRichTextContent(),
				newAttachments: attachments.attachments,
				savedAttachments: [...templateAttachments, ...remainingStepAttachments],
				sendAttachmentsAsLink: canSendFileLinks && sendAttachmentsAsLink,
				subject,
				templateReference: createTemplateReference(),
			},
		});

	const onTestEmailModalRequestClose = (result?: ITestEmailResult) => {
		setIsTestEmailModalOpen(false);
		if (result?.success) {
			toast.success('Test email sent.');
		}
	};

	const onSaveButtonClicked = async () => {
		const newContent = validateDraft('saving');
		if (!newContent) {
			return;
		}

		let lastDeletionResult: Api.IEmailAutomationStep | Api.IHtmlNewsletterAutomationStep | undefined;
		for (const attachment of pendingDeletions) {
			try {
				lastDeletionResult = await attachmentDeleteMutation.mutateAsync({
					templateId: automationTemplateId,
					stepId: step.id,
					attachmentId: attachment.id,
					impersonationContext,
				});
			} catch (error) {
				logApiError('DeleteEmailAutomationStepAttachment-Error', error);
				errorMessages.pushApiError(error);
				return;
			}
		}
		if (lastDeletionResult) {
			onAttachmentRemoved?.(lastDeletionResult);
		}

		const newTemplateReference = createTemplateReference();

		let newStep = pendingDeletions.length > 0 ? { ...step, attachments: remainingStepAttachments } : step;

		if (attachments?.count > 0) {
			try {
				newStep = await attachmentCreateMutation.mutateAsync({
					templateId: automationTemplateId,
					stepId: step.id,
					attachmentFiles: attachments.attachments,
					impersonationContext,
				});
			} catch (error) {
				logApiError('SaveEamilAutomationStepAttachments-Error', Api.asApiError(error));
				errorMessages.pushApiError(Api.asApiError(error));
				return;
			}
		}

		if (templateAttachments.length > 0) {
			newStep = {
				...newStep,
				attachments: [...templateAttachments],
			};
		}

		newStep = {
			...newStep,
			...(syncSubjectWithStep && {
				name: subject,
				subject,
			}),
			content: newContent,
			options: {
				subject,
				templateReference: newTemplateReference,
			},
			sendAttachmentsAsLink: sendAttachmentsAsLink || undefined,
		};
		stepUpdateMutation.mutate({
			templateId: automationTemplateId,
			stepId: step.id,
			impersonationContext,
			step: newStep,
		});
		onStepChanged?.();
	};

	return (
		<ReactModal
			onRequestClose={() => onRequestClose(null, true)}
			isOpen={true}
			className={css(styleSheet.modalContent)}
			portalClassName={css(styleSheet.modalRoot)}
			overlayClassName={css(styleSheet.modalOverlay)}
			shouldCloseOnOverlayClick={false}
		>
			<div className={css(styleSheet.modalHeader)} onContextMenu={stopModalContextMenuPropagation}>
				<h2 className={css(styleSheet.modalHeaderTitle)}>Edit Email</h2>
				<CloseButton onClick={() => onRequestClose(null, true)} fillColor='white' />
			</div>
			<div className={css(styleSheet.modalBody)} onContextMenu={stopModalContextMenuPropagation}>
				<div className={css(styleSheet.leftContainer)}>
					<div className={css(styleSheet.headerRow)}>
						<h3 className={css(styleSheet.header)}>{`${!readonly ? 'Edit' : 'View'} Email`}</h3>
						{canSendTestEmail ? <SendATestButton onClick={onSendTestButtonClicked} disabled={saving} /> : null}
					</div>
					<div className={css(styleSheet.body)}>
						<div className={css(styleSheet.bodyInputs)}>
							{!readonly && canShowAiContentAssistant ? (
								<AIAssistantButton
									onClick={() => setShowingAiContentGenWizard(true)}
									disabled={showingAiContentGenWizard}
								>
									Write with AI
								</AIAssistantButton>
							) : null}
							<Provider impersonationContext={impersonationContext}>
								<EmailComposerContext.Provider value={composerContext}>
									<EmailComposer
										attachments={attachments}
										bodyEditorState={emailBodyEditorState}
										bodyError={messageFieldError}
										bodyTemplate={emailMessageTemplate}
										className={css(styleSheet.bodyEditor)}
										hideBodyFooterToolbar={true}
										onBodyEditorFocusChanged={clearErrorMessages}
										onBodyEditorStateChanged={onMessageEditorStateChanged}
										onBodyFieldScroll={clearErrorMessages}
										onBodyTemplateChanged={onEmailMessageTemplateChanged}
										onRequestRemoveSavedEmailAttachment={
											!readonly ? handleRequestRemoveSavedEmailAttachment : undefined
										}
										onCustomFileChanged={onCustomFileChanged}
										onSubjectChanged={onSubjectChanged}
										onToggleSendAttachmentsAsLink={canSendFileLinks ? handleToggleSendAttachmentsAsLink : undefined}
										sendAttachmentsAsLink={canSendFileLinks ? sendAttachmentsAsLink : undefined}
										sendAsLinkRequired={canSendFileLinks ? isTotalSizeLarge : undefined}
										readonly={readonly}
										savedAttachments={[...templateAttachments, ...remainingStepAttachments]}
										showCcField={false}
										subject={subject}
										subjectError={subjectFieldError}
										hideBodyTemplateSelectorButton={readonly}
									/>
								</EmailComposerContext.Provider>
							</Provider>
						</div>
						{!!saving && <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />}
					</div>
					<div className={css(baseStyleSheet.horizontalStack, styleSheet.footer)}>
						<button
							className={css(baseStyleSheet.ctaButton)}
							onClick={!readonly ? onSaveButtonClicked : onCancelButtonClicked}
						>
							<span>{!readonly ? 'Save' : 'Close'}</span>
						</button>
						{!readonly ? (
							<>
								{(!isAdminView || impersonationContext?.user) && (
									<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onSaveAsTemplateButtonClicked}>
										<span>Save as Template</span>
									</button>
								)}
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={onCancelButtonClicked}>
									<span>Cancel</span>
								</button>
							</>
						) : null}
					</div>
					{canSendTestEmail && isTestEmailModalOpen ? (
						<TestEmailModal
							isOpen={isTestEmailModalOpen}
							notice={getAutomationStepTestEmailNotice(emailBodyEditorState.getRawRichTextContent(), subject)}
							onRequestClose={onTestEmailModalRequestClose}
							onSendTestEmail={onSendTestEmail}
						/>
					) : null}
					<Modal
						isOpen={showSaveTemplateModal && !readonly}
						onRequestClose={handleTemplateSaveCancel}
						useDefaultHeader
						className={css(styleSheet.saveTemplateModal)}
						shouldCloseOnOverlayClick={false}
					>
						{showSaveTemplateModal && !readonly ? (
							<SaveTemplate
								template={{
									content: emailBodyEditorState.getRawRichTextContent(),
									subject,
									templateType: Api.TemplateType.Email,
									attachments: step.attachments ?? [],
								}}
								onSuccess={handleTemplateSaveSuccess}
								onCancel={handleTemplateSaveCancel}
								impersonationContext={impersonationContext}
								enableKeywords
							/>
						) : null}
					</Modal>
				</div>
				<div className={css(styleSheet.rightContainer)}>
					{/* We handle conitional rendering within the component, so we can manage the chat session. Don't wrap this in a conditional. */}
					<UniversalAssistantChatWithinComposer
						isOpen={showingAiContentGenWizard}
						impersonationContext={impersonationContext}
						onUseAIEmail={onUseAIEmail}
						onClose={() => setShowingAiContentGenWizard(false)}
						emailContext={{
							subject,
							body: emailBodyEditorState.getRawRichTextContent(),
						}}
					/>
					{!showingAiContentGenWizard ? (
						<div className={css(styleSheet.rightContainerPlaceholder)}>
							<img src={EmailAutomationGraphic} className={css(styleSheet.emailAutomationGraphic)} />
							<p className={css(styleSheet.rightInfoText)}>
								For any email that is part of an automation, the default signature of the sender will be automatically
								used.
							</p>
						</div>
					) : null}
				</div>
			</div>
			<NotifyAttachmentsUnionModal
				isOpen={isNotifyAttachmentsUnionModalOpen}
				onRequestClose={() => setIsNotifyAttachmentsUnionModalOpen(false)}
			/>
		</ReactModal>
	);
}
